import React, { useState } from 'react';
import {
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ChatCircleText,
  CheckCircle,
  Prohibit,
  Warning,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { Contact } from '../../../types/crm';
import api from '../../../lib/api';

interface SmsConsentChipProps {
  contact: Contact;
  onRecorded?: () => void;
}

/**
 * Whether this contact may be texted, and on what basis.
 *
 * Carriers and the TCPA require a record of affirmative opt-in before the
 * first message, so a rep needs to see the answer before they start typing —
 * not discover it after the fact. Web forms and inbound texts record consent
 * on their own; the button here covers consent given out loud in the field.
 */
export const SmsConsentChip: React.FC<SmsConsentChipProps> = ({ contact, onRecorded }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const optedOut = contact.do_not_text;
  const consented = contact.sms_consent && !optedOut;

  const handleRecord = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/crm/contacts/${contact.id}/record-sms-consent/`, { note });
      setOpen(false);
      setNote('');
      onRecorded?.();
    } catch (err: any) {
      setError(err?.responseData?.error || 'Could not record consent.');
    } finally {
      setSaving(false);
    }
  };

  if (optedOut) {
    return (
      <Tooltip title="This contact replied STOP. They must opt in again themselves.">
        <Chip size="sm" variant="soft" color="danger" startDecorator={<Prohibit size={14} />}>
          Opted out
        </Chip>
      </Tooltip>
    );
  }

  if (consented) {
    const when = contact.sms_consent_at
      ? format(new Date(contact.sms_consent_at), 'MMM d, yyyy')
      : null;
    const how = contact.sms_consent_source_display || 'recorded';
    return (
      <Tooltip
        title={
          contact.sms_consent_text
            ? `${how}${when ? ` on ${when}` : ''} — "${contact.sms_consent_text}"`
            : `${how}${when ? ` on ${when}` : ''}`
        }
      >
        <Chip size="sm" variant="soft" color="success" startDecorator={<CheckCircle size={14} />}>
          Consented{when ? ` · ${when}` : ''}
        </Chip>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="No opt-in on file. Record consent before texting this contact.">
        <Chip
          size="sm"
          variant="soft"
          color="warning"
          startDecorator={<Warning size={14} />}
          onClick={() => setOpen(true)}
          sx={{ cursor: 'pointer' }}
        >
          No consent
        </Chip>
      </Tooltip>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog sx={{ width: 480, maxWidth: '95vw' }}>
          <ModalClose />
          <DialogTitle>
            <ChatCircleText size={20} />
            Record SMS consent
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography level="body-sm" sx={{ mb: 2 }}>
              Only record consent this contact actually gave — for example, they
              agreed out loud during an inspection to receive text updates.
              You are attesting to it, and your name is saved with the record.
            </Typography>
            <FormControl>
              <FormLabel>How was consent given? (optional)</FormLabel>
              <Textarea
                minRows={2}
                placeholder="e.g. Agreed to text updates during the roof inspection on Tuesday."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <FormHelperText>
                Saved to the contact's notes as an audit trail.
              </FormHelperText>
            </FormControl>
            {error && (
              <Typography level="body-sm" sx={{ color: 'danger.500', mt: 1.5 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button loading={saving} onClick={handleRecord}>
              Record consent
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default SmsConsentChip;
